/* What the app does when the server says the session — or the account — is gone. */

import { navigate } from '../router.js';
import { deleteDatabase } from '../db/idb.js';
import { offloadUserData } from '../study/userData.js';
import { clearAppVersion, clearSession, getAccessToken } from './storage.js';

/**
 * Handle a caught API error that turned out to be an expired / invalid token:
 * wipe the session and bounce to sign-in.
 *
 * Every authenticated screen needs this in its `catch`, and each one had its own
 * copy — so what counts as "the session ended" now has a single definition.
 *
 * Note it deliberately does *not* touch IndexedDB: the cached chapters, topics and
 * audio survive, and so do the user's own rows (favorites, dictionary, completed
 * topics). That last part is where a **forced** sign-out differs from the drawer's
 * "Wyloguj": the deliberate one hands those three stores over to the account and
 * then clears them (`offloadUserData` in `study/userData.js`), which this path
 * cannot do — it runs precisely because the token needed to store a backup turned
 * out to be gone. So the data waits here on the device until the next real
 * sign-out, which is the only outcome that doesn't lose it.
 *
 * @param {?{status?: number}} error The error just caught.
 * @returns {boolean} `true` when it was an auth failure and the caller should stop
 *   what it was doing — the user is on their way to `/login`.
 */
export function handleExpiredSession(error) {
  if (error?.status !== 401) return false;
  clearSession();
  navigate('/login');
  return true;
}

/**
 * End the session because **the server said so** — `/study/main` came back with
 * `hardLogout` (see `views/home.js`, the one caller).
 *
 * The third ending in this file, and the only unasked-for one where the token still
 * works — so unlike `handleExpiredSession` it *can* back the data up first, and
 * therefore must: it runs `offloadUserData` exactly like the drawer's "Wyloguj"
 * (push the three user stores to the account, then clear them), because a
 * server-ordered sign-out is still a sign-out and the favorites, dictionary and
 * completions belong to the account. It is also not `clearAccountData`: the account
 * is still there and the content cache is worth keeping, so the device is left able
 * to sign straight back in without re-downloading a clip.
 *
 * Best-effort like the drawer's copy: a failed push (offline, 413) still signs the
 * user out, and `offloadUserData` clears **only** after the upload resolved, so
 * nothing is lost — the rows stay for the next real sign-out.
 *
 * The order itself is deliberately **not** persisted anywhere (unlike the
 * `hardUpdate` gate): it is a one-shot instruction, acted on the moment it is seen,
 * and the next `/study/main` after signing back in decides again.
 */
export async function signOutByServerOrder() {
  try {
    await offloadUserData(getAccessToken());
  } catch {
    /* backup didn't land — sign out anyway; the rows are untouched and still here */
  }
  clearSession();
  navigate('/login');
}

/**
 * Erase everything this device remembers about a **deleted** account: the session
 * (token, e-mail, any pending registration), the cached content version and the
 * entire IndexedDB database — chapters, topics, downloaded audio, thumbnails,
 * favorites, dictionary and progress.
 *
 * The mirror image of `handleExpiredSession`, and the one place where the two
 * differ on purpose: a sign-out keeps the local content, because it isn't tied to
 * the token and re-signing-in should not mean re-downloading every clip. A
 * deleted account has nothing to come back to, so the device is left as if the
 * app had just been installed.
 *
 * Called after `DELETE /api/v1/user` succeeded. Dropping the database is
 * best-effort: if IndexedDB refuses, the user is still signed out and sent to
 * `/login` rather than being stranded on a screen whose account no longer exists.
 */
export async function clearAccountData() {
  clearSession();
  clearAppVersion();
  try {
    await deleteDatabase();
  } catch {
    /* cache could not be dropped — the sign-out below still has to happen */
  }
}
